/**
 * Module-level singleton for cold-start notification deep links.
 *
 * On a cold launch the navigation tree (especially lazy-loaded inner navigators)
 * is not ready when getLastNotificationResponseAsync() resolves.  Rather than
 * retrying a 3-level nested navigate() call and losing the race, we store the
 * parsed intent here and let ProjectsScreen consume it once it mounts — at that
 * point the PlansStack navigator is fully initialized.
 */

export interface PendingNotificationNav {
  projectId: number;
  screen?:
    | 'ProjectSurveys'
    | 'ProjectChat'
    | 'ProjectMedications'
    | 'ProjectSmartReminders'
    | 'ProjectHealthAlerts'
    | 'ProjectFiles'
    | 'ProjectWeeklyBriefing'
    | 'ProjectCommunicate'
    | 'ProjectContacts';
  /** For chat_message pushes: room to pre-select inside ProjectCommunicate. */
  chatRoomId?: number;
  /** For weekly_briefing pushes: the briefing row to pre-select in the picker. */
  briefingId?: number;
  openPageName?: string;
  openSurveyName?: string;
  openQuestionName?: string;
  initialPrompt?: string;
  /** For proactive_gap push responses: the original question shown as read-only context
   *  so the user types their answer rather than re-submitting the question itself. */
  proactiveGapContext?: string;
  /** For reminder notification taps: the smart_key of the triggered reminder. */
  detailSmartKey?: string;
  /** For reminder notification taps: recurrence_rule.page_id from payload. */
  detailPageId?: string;
  /** For reminder/health alert detail sheet rendering. */
  detailKind?: 'smart_reminder' | 'health_alert';
  detailTitle?: string;
  detailBody?: string;
  detailOccurredAt?: string;
  detailAlertType?: string;
  detailContext?: Record<string, unknown>;
  /** For ProjectContacts: which ContactsScreen tab to open. */
  tab?: 'members' | 'contacts' | 'chat' | 'map';
  /** For member-request pushes: the member row to scroll to and open. */
  openMemberCognitoId?: string;
  /** For ProjectFiles: which tab to open ('documents' | 'notes' | 'inbox').
   *  For ProjectCommunicate: 'chat' | 'inbox'. */
  initialTab?: 'documents' | 'notes' | 'inbox' | 'chat';
  /** For document_request pushes: auto-open the upload sheet on the Documents tab. */
  openUpload?: boolean;
  /** For document_request pushes: the slug to persist as document_type on upload. */
  prefillDocumentType?: string;
  /** For document_request pushes: the human-readable label shown in the upload sheet. */
  prefillDocumentLabel?: string;
  /** For calendar_followup pushes: auto-open TemplateFillSheet with this template. */
  openTemplateId?: number;
}

let _pending: PendingNotificationNav | null = null;

type Listener = () => void;
const _listeners = new Set<Listener>();

export function setPendingNotificationNav(data: PendingNotificationNav): void {
  _pending = data;
  // Snapshot listeners — handlers may call consume + unsubscribe synchronously.
  Array.from(_listeners).forEach((l) => {
    try { l(); } catch { /* listener errors must not block other consumers */ }
  });
}

/** Reads and clears the pending intent — call once from ProjectsScreen on mount. */
export function consumePendingNotificationNav(): PendingNotificationNav | null {
  const data = _pending;
  _pending = null;
  return data;
}

/**
 * Subscribe to pending-nav writes. Fires AFTER setPendingNotificationNav has
 * stored the value, so the listener can call consumePendingNotificationNav()
 * to read+clear it.
 *
 * Needed because the cold-launch init effect in RootNavigator can store the
 * intent AFTER ProjectsScreen has already mounted and run its mount-only
 * consume effect — without this signal the intent would sit stale.
 */
export function subscribePendingNotificationNav(listener: Listener): () => void {
  _listeners.add(listener);
  return () => { _listeners.delete(listener); };
}
